import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Star, Flame, Video, Target, MessageSquare, Heart
} from 'lucide-react';
import { format } from 'date-fns';
import { ComprehensiveUserProfile } from './types';

interface EngagementTabProps {
  profile: ComprehensiveUserProfile;
}

export const EngagementTab: React.FC<EngagementTabProps> = ({ profile }) => {
  const { engagement, communityStats } = profile;

  return (
    <div className="space-y-6">
      {/* Points and Streaks */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* ThrivePoints */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Star className="h-4 w-4 text-yellow-500" />
              ThrivePoints
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center mb-4">
              <p className="text-4xl font-bold">{engagement?.thrivePoints?.total || 0}</p>
              <p className="text-sm text-muted-foreground">Total Points Earned</p>
            </div>
            {engagement?.thrivePoints?.breakdown?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-xs font-medium text-muted-foreground">Breakdown by Component</h4>
                {engagement.thrivePoints.breakdown.map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center p-2 rounded bg-muted/50">
                    <span className="text-sm">{item.component}</span>
                    <span className="font-medium">{item.points}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Streaks */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Flame className="h-4 w-4 text-orange-500" />
              Activity Streaks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {engagement?.streaks?.length > 0 ? (
              <div className="space-y-3">
                {engagement.streaks.map((streak, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-sm">{streak.type?.replace(/_/g, ' ')}</span>
                      <Badge variant={streak.currentCount > 0 ? 'default' : 'secondary'}>
                        {streak.currentCount > 0 ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-2 gap-4 text-center">
                      <div>
                        <p className="text-2xl font-bold text-orange-500">{streak.currentCount}</p>
                        <p className="text-xs text-muted-foreground">Current Streak</p>
                      </div>
                      <div>
                        <p className="text-2xl font-bold">{streak.longestCount}</p>
                        <p className="text-xs text-muted-foreground">Longest Streak</p>
                      </div>
                    </div>
                    {streak.lastCompletedAt && (
                      <p className="text-xs text-muted-foreground text-center mt-2">
                        Last completed: {format(new Date(streak.lastCompletedAt), 'MMM d, yyyy')}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center">No streaks recorded</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Video Activity */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Video className="h-4 w-4 text-purple-500" />
            Video Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50 text-center">
              <p className="text-3xl font-bold">{engagement?.videoActivity?.totalWatched || 0}</p>
              <p className="text-sm text-muted-foreground">Videos Watched</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50 text-center">
              <p className="text-3xl font-bold">{engagement?.videoActivity?.avgCompletionRate || 0}%</p>
              <p className="text-sm text-muted-foreground">Avg. Completion Rate</p>
            </div>
            <div className="p-4 rounded-lg bg-muted/50 border border-border/50">
              <p className="text-sm font-medium mb-2">Categories Watched</p>
              <div className="flex flex-wrap gap-1">
                {engagement?.videoActivity?.categories?.length > 0 ? (
                  engagement.videoActivity.categories.map((cat, idx) => (
                    <Badge key={idx} variant="outline" className="text-xs">{cat}</Badge>
                  ))
                ) : (
                  <span className="text-sm text-muted-foreground">No categories</span>
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Monthly Goals */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Target className="h-4 w-4 text-green-500" />
            Monthly Goals
          </CardTitle>
        </CardHeader>
        <CardContent>
          {engagement?.monthlyGoals?.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {engagement.monthlyGoals.map((goal, idx) => {
                const progress = goal.goal > 0 ? Math.min(100, (goal.achieved / goal.goal) * 100) : 0;
                return (
                  <div key={idx} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                    <p className="text-sm font-medium mb-2">
                      {format(new Date(goal.year, goal.month - 1), 'MMM yyyy')}
                    </p>
                    <div className="mb-2">
                      <div className="flex justify-between text-xs mb-1">
                        <span>{goal.achieved} / {goal.goal}</span>
                        <span>{Math.round(progress)}%</span>
                      </div>
                      <Progress value={progress} className="h-2" />
                    </div>
                    <Badge variant={progress >= 100 ? 'default' : 'secondary'} className="text-xs">
                      {progress >= 100 ? 'Completed' : 'In Progress'}
                    </Badge>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center">No monthly goals recorded</p>
          )}
        </CardContent>
      </Card>

      {/* Community Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <MessageSquare className="h-4 w-4 text-blue-500" />
            Community Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <p className="text-2xl font-bold">{communityStats?.postsCreated || 0}</p>
            <p className="text-sm text-muted-foreground">Total Posts Created</p>
          </div>
          {communityStats?.recentPosts?.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-medium text-muted-foreground">Recent Posts</h4>
              {communityStats.recentPosts.map((post) => (
                <div key={post.id} className="p-3 rounded-lg bg-muted/50 border border-border/50">
                  <div className="flex justify-between items-start mb-1">
                    <Badge variant={post.isPublic ? 'default' : 'secondary'} className="text-xs">
                      {post.isPublic ? 'Public' : 'Private'}
                    </Badge>
                    <span className="text-xs text-muted-foreground">
                      {post.createdAt ? format(new Date(post.createdAt), 'MMM d, yyyy') : ''}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">{post.content}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
